import React, { useRef, useState } from "react";
import { X, Upload } from "lucide-react";
import {
  uploadPatientDocument,
  deletePatientDocument,
  viewPatientDocument,
} from "../APIs/patientDocumentApi";

const TYPE_OPTIONS = [
  { value: "GOVERNMENT_ID", label: "Government ID" },
  { value: "INTAKE_FORM", label: "Intake Form" },
  { value: "CLINICAL_DOCUMENT", label: "Clinical Supporting Document" },
];

export default function PatientDocumentModal({
  patientId,
  usedTypes,
  document,
  onClose,
  onSaved,
}) {
  const isEdit = !!document;

  const [docType, setDocType] = useState(document?.documentType ?? "");
  const [doesNotExpire, setDoesNotExpire] = useState(!document?.expiresAt);
  const [expiresAt, setExpiresAt] = useState(
    document?.expiresAt?.slice(0, 10) ?? "",
  );
  const [notes, setNotes] = useState(document?.notes ?? "");
  const [file, setFile] = useState(null);
  const [replaceFile, setReplaceFile] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef();

  const availableTypes = TYPE_OPTIONS.filter(
    (t) => !usedTypes.includes(t.value) || t.value === document?.documentType,
  );

  const handleFile = (f) => {
    if (f.size > 5 * 1024 * 1024) {
      setError("File must be under 5 MB");
      return;
    }
    setFile(f);
    setError("");
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const handleSubmit = async () => {
    if (!docType) {
      setError("Select a document type");
      return;
    }
    if (!isEdit && !file) {
      setError("Please upload a file");
      return;
    }
    if (isEdit && replaceFile && !file) {
      setError("Please select a replacement file");
      return;
    }

    setSaving(true);
    setError("");
    try {
      if (isEdit && replaceFile && file) {
        await deletePatientDocument(document.documentId, patientId);
        await uploadPatientDocument(
          patientId,
          file,
          docType,
          docType,
          doesNotExpire ? null : expiresAt,
        );
      } else if (!isEdit) {
        await uploadPatientDocument(
          patientId,
          file,
          docType,
          docType,
          doesNotExpire ? null : expiresAt,
        );
      }
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">
            {isEdit ? "Edit Document" : "Add Document"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Document Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Document Type
            </label>
            <select
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              disabled={isEdit}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-50 disabled:text-gray-500"
            >
              <option value="">Select type...</option>
              {availableTypes.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          {/* Expiration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Expiration Date
            </label>
            <input
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              disabled={doesNotExpire}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-50 disabled:text-gray-400"
            />
            <label className="flex items-center gap-2 mt-2 text-sm text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={doesNotExpire}
                onChange={(e) => setDoesNotExpire(e.target.checked)}
                className="rounded"
              />
              Does not expire
            </label>
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Document File
            </label>
            {isEdit && !replaceFile ? (
              <div className="flex items-center gap-2 border rounded-lg px-3 py-2 bg-gray-50">
                <span className="text-sm text-gray-700 truncate flex-1">
                  {document.fileName}
                </span>
                <button
                  onClick={async () => {
                    try {
                      const url = await viewPatientDocument(
                        document.documentId,
                      );
                      window.open(url, "_blank", "noopener,noreferrer");
                    } catch {
                      alert("Unable to load document.");
                    }
                  }}
                  className="text-gray-400 hover:text-teal-600 text-xs"
                >
                  View
                </button>
                <button
                  onClick={() => setReplaceFile(true)}
                  className="text-xs text-red-500 hover:text-red-700"
                >
                  Replace
                </button>
              </div>
            ) : (
              <div
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => inputRef.current.click()}
                className="border-2 border-dashed border-gray-300 rounded-lg px-4 py-6 flex flex-col items-center gap-1 cursor-pointer hover:border-teal-400 transition"
              >
                <Upload className="w-5 h-5 text-gray-400" />
                {file ? (
                  <p className="text-sm text-teal-600 font-medium">
                    {file.name}
                  </p>
                ) : (
                  <>
                    <p className="text-sm text-gray-500">
                      Drag & drop or click to upload
                    </p>
                    <p className="text-xs text-gray-400">
                      PDF, JPG, PNG · max 5 MB
                    </p>
                  </>
                )}
                <input
                  ref={inputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files[0]) handleFile(e.target.files[0]);
                  }}
                />
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Any additional notes..."
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
            />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border rounded-lg text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-40"
          >
            {saving ? "Saving..." : isEdit ? "Save Changes" : "Upload"}
          </button>
        </div>
      </div>
    </div>
  );
}
